import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Circle, ChevronRight, X, Sparkles, ArrowRight } from 'lucide-react';
import apiClient from '../services/api';
import { toast } from 'sonner';

// Steps whose completion is a "soft" engagement milestone — clicking the CTA
// counts as completing them.  Maps step key → track event name.
const SOFT_STEP_EVENTS = {
  margin_guard_brief: 'viewed_margin_guard',
  activate_agent: 'viewed_agents',
  optimize_distribution: 'viewed_distribution',
  review_ci_score: 'viewed_ci_score',
};

/**
 * 7-Day Onboarding Journey widget.
 *
 * Renders on the dashboard for merchants who haven't finished or dismissed
 * the journey.  Auto-detected steps (connect store, Slack, first deal) update
 * on their own; soft steps complete when the merchant engages.
 */
export default function OnboardingJourney() {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadJourney();
  }, []);

  const loadJourney = async () => {
    try {
      const res = await apiClient.get('/api/onboarding/journey');
      setJourney(res.data);
    } catch (err) {
      console.error('[OnboardingJourney] load failed', err);
    }
    setLoading(false);
  };

  const handleStepCTA = async (step) => {
    const event = SOFT_STEP_EVENTS[step.key];
    if (event && step.status === 'pending') {
      // Engagement counts as completion for soft steps
      try {
        await apiClient.post('/api/onboarding/journey/track', { event });
      } catch (err) {
        console.error('[OnboardingJourney] track failed', err);
      }
    }
    navigate(step.cta_link);
  };

  const handleMarkDone = async (stepKey) => {
    try {
      const res = await apiClient.post(`/api/onboarding/journey/step/${stepKey}`, {
        status: 'completed',
      });
      setJourney(res.data);
    } catch (err) {
      toast.error('Failed to update step');
    }
  };

  const handleSkip = async (stepKey) => {
    try {
      const res = await apiClient.post(`/api/onboarding/journey/step/${stepKey}`, {
        status: 'skipped',
      });
      setJourney(res.data);
    } catch (err) {
      toast.error('Failed to skip step');
    }
  };

  const handleDismiss = async () => {
    try {
      await apiClient.post('/api/onboarding/journey/dismiss');
      setJourney({ ...journey, dismissed: true });
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  if (loading || !journey) return null;
  if (journey.dismissed) return null;

  // Completed journey: show a brief celebration card (still dismissible)
  if (journey.completed) {
    return (
      <div className="mb-6 p-5 rounded-xl bg-gradient-to-r from-[#3EF1C7]/10 to-[#7A5CFF]/10 border border-[#3EF1C7]/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#3EF1C7]/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#3EF1C7]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">You're fully activated</h3>
            <p className="text-sm text-[#9CB0C7]">
              All 7 setup steps complete. Your Agentic Commerce OS is running.
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl bg-[#0E1426] border border-[#1E3A5F] overflow-hidden">
      {/* Header */}
      <div className="p-5 flex items-center justify-between border-b border-[#1E3A5F]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3EF1C7] to-[#7A5CFF] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#0A1628]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Get started — your first 7 days</h3>
            <p className="text-sm text-[#9CB0C7]">
              {journey.steps_done} of {journey.steps_total} complete · Day {journey.current_day} of 7
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="px-3 py-1.5 rounded-md text-sm text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
          <button
            onClick={handleDismiss}
            className="p-2 rounded-md text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F]/50 transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 pt-4">
        <div className="h-2 rounded-full bg-[#1E3A5F] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] transition-all duration-500"
            style={{ width: `${journey.progress_pct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      {expanded && (
        <div className="p-5 space-y-2">
          {journey.steps.map((step) => (
            <StepRow
              key={step.key}
              step={step}
              onCTA={() => handleStepCTA(step)}
              onMarkDone={() => handleMarkDone(step.key)}
              onSkip={() => handleSkip(step.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StepRow({ step, onCTA, onMarkDone, onSkip }) {
  const isDone = step.status === 'completed';
  const isSkipped = step.status === 'skipped';
  const isPending = step.status === 'pending';

  return (
    <div
      className={`p-4 rounded-lg border transition-colors ${
        step.is_today && isPending
          ? 'bg-[#1E3A5F]/40 border-[#3EF1C7]/40'
          : 'bg-[#0A1628] border-[#1E3A5F]'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className="mt-0.5 flex-shrink-0">
          {isDone ? (
            <CheckCircle className="w-5 h-5 text-[#3EF1C7]" />
          ) : isSkipped ? (
            <Circle className="w-5 h-5 text-[#6B7F95]" />
          ) : (
            <Circle className="w-5 h-5 text-[#9CB0C7]" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-[#6B7F95] uppercase tracking-wider">
              Day {step.day}
            </span>
            {step.is_today && isPending && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#3EF1C7]/20 border border-[#3EF1C7]/40 text-[#3EF1C7] font-semibold">
                TODAY
              </span>
            )}
            {step.auto_detected && isDone && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                AUTO-DETECTED
              </span>
            )}
            {isSkipped && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1E3A5F] text-[#6B7F95]">
                SKIPPED
              </span>
            )}
          </div>
          <h4
            className={`font-semibold mt-0.5 ${
              isDone ? 'text-[#9CB0C7] line-through' : 'text-white'
            }`}
          >
            {step.title}
          </h4>
          {isPending && (
            <>
              <p className="text-sm text-[#9CB0C7] mt-1">{step.description}</p>
              <p className="text-xs text-[#3EF1C7] mt-1.5">→ {step.value_line}</p>
            </>
          )}
        </div>

        {/* Actions */}
        {isPending && (
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <button
              onClick={onCTA}
              className="px-3 py-2 rounded-md bg-[#3EF1C7] text-[#0A1628] text-sm font-semibold hover:bg-[#3EF1C7]/90 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              {step.cta_label}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={onMarkDone}
                className="text-xs text-[#9CB0C7] hover:text-white transition-colors"
              >
                Mark done
              </button>
              <span className="text-[#1E3A5F]">·</span>
              <button
                onClick={onSkip}
                className="text-xs text-[#6B7F95] hover:text-[#9CB0C7] transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
